import type { ChangeEvent } from 'react';
import React from 'react';
import { RadioWithLabel } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import Link from 'next/link';
import { useRouter } from 'next/router';

import * as styles from './RadioButtonLinkGroup.styles';

const doNothing = () => undefined;

type RadioButtonLinkData = {
  checked: boolean;
  crawlable?: boolean;
  disabled?: boolean;
  href?: string;
  labelText: string;
  secondaryLabelText?: string;
  value: string;
};

export interface RadioButtonLinkGroupProps {
  accessibleLabel: string;
  accessibleLabelElementId?: string;
  accessibleLabelElementType?: keyof JSX.IntrinsicElements;
  hideLabel?: boolean;
  name: string;
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  radioButtonData: RadioButtonLinkData[];
}

interface CrawlableLinkProps {
  accessibleName: string;
  active: boolean;
  href: string;
  isNavigationOnly: boolean;
  testId: string;
}

const CrawlableLink = ({
  accessibleName,
  active,
  href,
  isNavigationOnly,
  testId,
}: CrawlableLinkProps) => (
  <Link
    aria-current={isNavigationOnly && active ? 'true' : undefined}
    aria-hidden={isNavigationOnly ? undefined : 'true'}
    css={[styles.linkUnderlay, !isNavigationOnly && styles.crawlOnlyUnderlay]}
    data-testid={testId}
    href={href}
    prefetch={false}
    scroll={false}
    shallow
    tabIndex={isNavigationOnly ? undefined : -1}
  >
    <span css={visuallyHidden}>{accessibleName}</span>
  </Link>
);

interface NonCrawlableButtonProps {
  accessibleName: string;
  active: boolean;
  href: string;
  testId: string;
}

// `useRouter` is scoped here so the parent doesn't depend on a Next.js
// RouterContext unless this branch actually renders.
const NonCrawlableButton = ({ accessibleName, active, href, testId }: NonCrawlableButtonProps) => {
  const router = useRouter();
  return (
    <button
      aria-current={active ? 'true' : undefined}
      css={styles.linkUnderlay}
      data-href={href}
      data-testid={testId}
      onClick={() => router.push(href, undefined, { shallow: true, scroll: false })}
      type="button"
    >
      <span css={visuallyHidden}>{accessibleName}</span>
    </button>
  );
};

export const RadioButtonLinkGroup = ({
  name,
  accessibleLabel,
  accessibleLabelElementType: AccessibleLabelElementTag = 'p',
  accessibleLabelElementId = `${name}-label`,
  hideLabel = false,
  onChange,
  radioButtonData,
}: RadioButtonLinkGroupProps) => {
  return (
    <div aria-labelledby={accessibleLabelElementId} role="group">
      <span css={hideLabel ? visuallyHidden : undefined} id={accessibleLabelElementId}>
        <AccessibleLabelElementTag>{accessibleLabel}</AccessibleLabelElementTag>
      </span>
      {radioButtonData.map(
        ({
          checked,
          crawlable,
          disabled,
          href,
          labelText,
          secondaryLabelText,
          value: radioButtonValue,
        }) => {
          const hasNavigableHref = Boolean(href && !disabled);
          const isNavigationOnly = hasNavigableHref && !onChange;
          const accessibleName = `${labelText}${
            secondaryLabelText ? ` ${secondaryLabelText}` : ''
          }`;

          let navigationElement: React.ReactNode = null;
          if (hasNavigableHref && crawlable) {
            navigationElement = (
              <CrawlableLink
                accessibleName={accessibleName}
                active={checked}
                href={href!}
                isNavigationOnly={isNavigationOnly}
                testId={`${name}-${radioButtonValue}-link`}
              />
            );
          } else if (isNavigationOnly) {
            navigationElement = (
              <NonCrawlableButton
                accessibleName={accessibleName}
                active={checked}
                href={href!}
                testId={`${name}-${radioButtonValue}-button`}
              />
            );
          }

          const radioButton = (
            <>
              {navigationElement}
              <span css={styles.radioButtonForeground}>
                <RadioWithLabel
                  checked={checked}
                  disabled={disabled}
                  label={labelText}
                  name={name}
                  onChange={(e) => (onChange ? onChange(radioButtonValue, e) : doNothing())}
                  value={radioButtonValue}
                />
              </span>
            </>
          );

          const wrappedRadioButton = isNavigationOnly ? (
            <span css={styles.radioButtonRow}>{radioButton}</span>
          ) : (
            radioButton
          );

          return (
            <div
              css={styles.radioButtonWrapperContainer}
              data-testid={radioButtonValue}
              key={radioButtonValue}
            >
              {wrappedRadioButton}
              {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
            </div>
          );
        },
      )}
    </div>
  );
};
